import type { Promisable } from "@core/foundation/promise";
import { hasValue } from "@core/foundation/types";

export interface IndexedDbOperation {
  readonly run: (store: IDBObjectStore, fail: (error?: Error) => void) => void;
  readonly storeName: string;
}

export class IndexedDbClient {
  private readonly database: string;

  // @mutable
  private dbPromise: Promisable<IDBDatabase> | undefined = undefined;

  private readonly tables: readonly string[];

  private readonly version: number;

  public constructor({
    database,
    tables,
    version,
  }: {
    readonly database: string;
    readonly tables: readonly string[];
    readonly version: number;
  }) {
    this.database = database;
    this.tables = tables;
    this.version = version;
  }

  public connect(): Promisable<IDBDatabase> {
    this.dbPromise ??= new Promise((resolve, reject) => {
      const request = indexedDB.open(this.database, this.version);

      request.addEventListener("upgradeneeded", () => {
        const { result: db, transaction } = request;

        // oxlint-disable-next-line no-warning-comments
        // TODO(APT-2405): index only new stores once every device DB holds it.
        for (const table of this.tables) {
          const store =
            db.objectStoreNames.contains(table) && hasValue(transaction)
              ? transaction.objectStore(table)
              : db.createObjectStore(table, { keyPath: ["PK", "SK"] });

          if (!store.indexNames.contains("SK2")) {
            store.createIndex("SK2", ["PK", "SK2"]);
          }
        }
      });

      request.addEventListener("success", () => {
        resolve(request.result);
      });

      request.addEventListener("error", () => {
        reject(
          request.error ??
            new Error(`Failed to open the database ${this.database}.`),
        );
      });
    });

    return this.dbPromise;
  }

  public async transact(
    operations: readonly IndexedDbOperation[],
  ): Promise<{ readonly committed: boolean }> {
    if (operations.length === 0) {
      return { committed: true };
    }

    const db = await this.connect();

    const storeNames = [
      ...new Set(operations.map(operation => operation.storeName)),
    ] as const;

    const transaction = db.transaction(storeNames, "readwrite");

    return new Promise((resolve, reject) => {
      let failed = false;

      let failureError: Error | undefined;

      transaction.addEventListener("complete", () => {
        resolve({ committed: true });
      });

      transaction.addEventListener("abort", () => {
        if (hasValue(failureError)) {
          reject(failureError);

          return;
        }

        if (failed) {
          resolve({ committed: false });

          return;
        }

        reject(
          transaction.error ??
            new Error("The IndexedDB transaction was aborted."),
        );
      });

      const fail = (error?: Error): void => {
        if (!failed) {
          failed = true;
          failureError = error;
          transaction.abort();
        }
      };

      try {
        for (const operation of operations) {
          operation.run(transaction.objectStore(operation.storeName), fail);
        }
      } catch (error) {
        transaction.abort();

        throw error;
      }
    });
  }
}
